import { Json } from '@eagle/common';
import { StagePropertyDefinition } from '@eagle/core-data-types';
import { Stack, TextField, Typography } from '@ethings/ui-toolkit';
import { isUndefined } from 'lodash';
import { DateTime } from 'luxon';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_TIME_ZONE } from '../../../constants';
import { useResponsiveText } from '../../../hooks';
import { Nullable } from '../../../types';
import { DateErrors, DateTimeRangeError, innerText } from '../../../util';
import { DatePicker } from '../../date-time-range-picker/date-picker';
import { useStageConfirmDialogContext } from './stage-confirm-dialog-context';

interface StageDetailsEditorProps {
  onValidationChange?: (isValid: boolean) => void;
}

const isPersistFromPreviousStage = (
  persistFromPreviousStage: unknown,
  stageId: string,
): boolean => {
  if (!persistFromPreviousStage) return false;

  if (typeof persistFromPreviousStage === 'object') {
    return Boolean((persistFromPreviousStage as Record<string, boolean>)[stageId]);
  } else if (Array.isArray(persistFromPreviousStage)) {
    return persistFromPreviousStage.includes(stageId);
  }

  return false;
};

interface PropertyInfo {
  definition?: StagePropertyDefinition;
  isPersisted: boolean;
  isRequired: boolean;
  value: Json;
}

interface PropertyScope {
  requiredProperties: string[];
  order: string[];
  properties: Record<string, PropertyInfo>;
}

export const StageDetailsEditor: FC<StageDetailsEditorProps> = ({ onValidationChange }) => {
  const { t } = useTranslation(['common', 'admin']);
  const { state, dispatch, stage, lifeCycleState } = useStageConfirmDialogContext();
  const [persistedFields, setPersistedFields] = useState<Record<string, Record<string, boolean>>>({});
  const [dateErrors, setDateErrors] = useState<Record<string, string>>({});

  const titleText = t('admin:page.thing-detail.update-stage-dialog.stage-details.labels');
  const h6Styles = useResponsiveText('1.25rem', innerText(titleText).length, {
    minFontSize: '1rem',
    maxFontSize: '1.25rem',
    textLengthThreshold: 20,
  });

  const availableScopes = useMemo(() => {
    if (!stage.stageProperties || Object.keys(stage.stageProperties).length === 0) {
      return {};
    }

    const scopes: Record<string, PropertyScope> = {};

    for (const [scopeName, scopeDefinition] of Object.entries(stage.stageProperties)) {
      if (!state.addOns?.[scopeName]) {
        continue;
      }

      const requiredProperties = scopeDefinition.requiredProperties || [];
      const order = scopeDefinition.order || [];
      const properties: Record<string, PropertyInfo> = {};

      for (const propertyName of order) {
        const definition = scopeDefinition.definitions?.[propertyName] as StagePropertyDefinition | undefined;
        const isRequired = requiredProperties.includes(propertyName);
        const value = state.stageProperties?.[scopeName]?.[propertyName]?.value ?? '';

        properties[propertyName] = {
          definition,
          isRequired,
          isPersisted: false,
          value,
        };
      }

      if (Object.keys(properties).length > 0) {
        scopes[scopeName] = {
          requiredProperties,
          order,
          properties,
        };
      }
    }

    return scopes;
  }, [stage.stageProperties, state.addOns, state.stageProperties]);

  useEffect(() => {
    if (!lifeCycleState?.stageId || Object.keys(availableScopes).length === 0) {
      return;
    }

    const currentStageId = lifeCycleState.stageId;
    const newPersistedFields: Record<string, Record<string, boolean>> = {};

    for (const [scopeName, scope] of Object.entries(availableScopes)) {
      newPersistedFields[scopeName] = {};

      for (const [propertyName, propertyInfo] of Object.entries(scope.properties)) {
        const definition = propertyInfo.definition;
        const persistFromPreviousStage = definition?.persistFromPreviousStage;

        newPersistedFields[scopeName][propertyName] = Boolean(
          persistFromPreviousStage &&
          isPersistFromPreviousStage(persistFromPreviousStage, currentStageId) &&
          state.stageProperties?.[scopeName]?.[propertyName]?.value,
        );
      }
    }

    setPersistedFields(newPersistedFields);
  }, [stage.stageProperties, state.stageProperties, lifeCycleState, availableScopes]);

  useEffect(() => {
    if (Object.keys(availableScopes).length === 0) {
      onValidationChange?.(false);
      return;
    }

    let isFormValid = true;

    for (const scope of Object.values(availableScopes)) {
      for (const [propertyName, propertyInfo] of Object.entries(scope.properties)) {
        if (propertyInfo.isRequired) {
          const scopeName = Object.keys(availableScopes).find(
            (key) => availableScopes[key].properties[propertyName] === propertyInfo,
          );

          if (!scopeName) continue;

          const hasValue = Boolean(state.stageProperties?.[scopeName]?.[propertyName]?.value);
          if (!hasValue) {
            isFormValid = false;
            break;
          }
        }
      }
      if (!isFormValid) break;
    }

    if (isFormValid && Object.keys(dateErrors).length > 0) {
      isFormValid = !Object.values(dateErrors).some((error) => error !== '');
    }

    onValidationChange?.(isFormValid);
  }, [state.stageProperties, availableScopes, onValidationChange, dateErrors]);

  const handleDateChange = (date: Nullable<DateTime>, scopeName: string, propertyName: string): void => {
    setDateErrors((prev) => ({ ...prev, [`${scopeName}.${propertyName}`]: '' }));

    if (!date) {
      setDateErrors((prev) => ({ ...prev, [`${scopeName}.${propertyName}`]: DateTimeRangeError.INVALID_DATE }));
      handlePropertyChange(`${scopeName}.${propertyName}`, '');
      return;
    }

    if (date.invalidReason) {
      const error = date.invalidReason === DateErrors.UNPARSABLE ? DateTimeRangeError.INVALID_FORMAT : DateTimeRangeError.INVALID_DATE;
      setDateErrors((prev) => ({ ...prev, [`${scopeName}.${propertyName}`]: error }));
      return;
    }

    handlePropertyChange(`${scopeName}.${propertyName}`, date.toISODate());

    if (propertyName === 'startDate' || propertyName === 'finishDate') {
      const otherProperty = propertyName === 'startDate' ? 'finishDate' : 'startDate';
      const otherDateValue = state.stageProperties?.[scopeName]?.[otherProperty]?.value;

      if (otherDateValue) {
        const currentDate = date.startOf('day');
        const otherDate = DateTime.fromISO(String(otherDateValue)).startOf('day');

        if (otherDate.isValid) {
          const isInvalidRange =
            (propertyName === 'startDate' && currentDate >= otherDate) ||
            (propertyName === 'finishDate' && currentDate <= otherDate);

          setDateErrors((prev) => ({
            ...prev,
            [`${scopeName}.finishDate`]: isInvalidRange ? DateTimeRangeError.INVALID_RANGE : '',
          }));
        }
      }
    }
  };

  const handlePropertyChange = useCallback(
    (path: string, value: Json) => {
      dispatch({ type: 'SET_STAGE_PROPERTY', path, value });
    },
    [dispatch],
  );

  if (Object.keys(availableScopes).length === 0) {
    return null;
  }

  const persistedValueHelper = t('common:component.stage-confirm-dialog.hint.persisted-value');

  return (
    <Stack spacing={2}>
      <Typography
        variant="h6"
        style={{
          fontSize: h6Styles.fontSize,
          lineHeight: h6Styles.lineHeight,
          wordBreak: h6Styles.wordBreak,
          whiteSpace: h6Styles.whiteSpace,
          overflow: h6Styles.overflow,
          textOverflow: h6Styles.textOverflow,
        }}
      >
        {titleText}
      </Typography>
      {Object.entries(availableScopes).map(([scopeName, scope]) => {
        return (
          <Stack key={scopeName} spacing={2}>
            {scope.order.map((propertyName) => {
              const propertyInfo = scope.properties[propertyName];
              if (!propertyInfo) return null;

              const value = state.stageProperties?.[scopeName]?.[propertyName]?.value ?? '';
              const isPersisted = persistedFields[scopeName]?.[propertyName] || false;
              const isRequired = propertyInfo.isRequired;
              const definition = propertyInfo.definition;

              const label = definition?.label || t(`common:common.labels.${propertyName}`, {
                defaultValue: propertyName.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase()),
              });

              const propertyType = definition?.type || 'text';

              if (propertyType === 'date') {
                const dateError = dateErrors[`${scopeName}.${propertyName}`]?.trim();
                return (
                  <DatePicker
                    key={`${scopeName}.${propertyName}`}
                    data-testid={`stage-dialog-${scopeName}-${propertyName}`}
                    label={label}
                    date={isUndefined(value) || value === null ? null : DateTime.fromISO(String(value), { zone: DEFAULT_TIME_ZONE })}
                    onChange={(newDate) => {
                      if (!newDate && !propertyInfo.isRequired) {
                        handlePropertyChange(`${scopeName}.${propertyName}`, '');
                      } else {
                        handleDateChange(newDate, scopeName, propertyName);
                      }
                    }}
                    helperText={dateError && dateError.length > 0 ? (
                      <Typography variant="caption" color="error" data-testid="date-picker-error-container">
                        {t(`common:component.date-picker.hint.${dateError}`)}
                      </Typography>
                    ) : isPersisted ? (persistedValueHelper) : undefined
                    }
                    required={isRequired}
                    dateError={dateErrors[`${scopeName}.${propertyName}`]}
                  />
                );
              }

              return (
                <TextField
                  key={`${scopeName}.${propertyName}`}
                  data-testid={`stage-dialog-${scopeName}-${propertyName}`}
                  label={label}
                  value={value || ''}
                  onChange={(e) => handlePropertyChange(`${scopeName}.${propertyName}`, e.target.value)}
                  helperText={isPersisted ? persistedValueHelper : undefined}
                  required={isRequired}
                  fullWidth
                  margin="none"
                  size="small"
                />
              );
            })}
          </Stack>
        );
      })}
    </Stack >
  );
};
